import React, { useMemo, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@heroicons/react/24/outline';
import { dietService } from '../../services/dietService';
import { Diet, DietType } from '../../types/diet';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DietModal from '../../components/Setup/DietModal';
import DeleteConfirmModal from '../../components/UI/DeleteConfirmModal';
import { useToast } from '../../components/UI/ToastContainer';
import BulkActions, { commonBulkActions } from '../../components/UI/BulkActions';
import DataGrid from '../../components/DataGrid';


const DIET_TYPE_LABELS: Record<DietType, string> = {
  PER_DAY: 'Per Day',
  PER_MEAL: 'Per Meal',
  HALF_BOARD_AM: 'Half Board AM',
  LUNCH_BOX: 'Lunch Box',
  HALF_BOARD_PM: 'Half Board PM',
};

const DietsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedDiet, setSelectedDiet] = useState<Diet | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Simple column filters (UI presence for parity; currently applied to client-side rows)
  const [filters, setFilters] = useState<{ name?: string; code?: string; status?: string }>({});
  const [typeFilter, setTypeFilter] = useState<DietType[]>([]);
  const DIET_TYPE_OPTIONS: { value: DietType; label: string }[] = [
    { value: 'PER_DAY', label: 'Per Day' },
    { value: 'PER_MEAL', label: 'Per Meal' },
    { value: 'HALF_BOARD_AM', label: 'Half Board AM' },
    { value: 'LUNCH_BOX', label: 'Lunch Box' },
    { value: 'HALF_BOARD_PM', label: 'Half Board PM' },
  ];


  const handleSelectOne = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const queryClientInvalidate = () => queryClient.invalidateQueries({ queryKey: ['diets'] });

  const handleBulkAction = async (actionKey: string, ids: string[]) => {
    try {
      if (ids.length === 0) return;
      if (actionKey === 'activate' || actionKey === 'deactivate') {
        await dietService.bulkUpdateDietStatus(ids, actionKey === 'activate' ? 'ACTIVE' : 'INACTIVE');
        showSuccess(actionKey === 'activate' ? 'Activated' : 'Deactivated', `${ids.length} diet(s) ${actionKey === 'activate' ? 'activated' : 'deactivated'}`);
      } else if (actionKey === 'delete') {
        for (const id of ids) await deleteMutation.mutateAsync(id);
        showSuccess('Deleted', `${ids.length} diet(s) deleted`);
      } else if (actionKey === 'refresh') {
        // no-op other than refreshing
      }
      setSelectedIds([]);
      queryClientInvalidate();
    } catch (e: any) {
      showError('Bulk action failed', e?.message || 'Please try again');
    }
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [dietToDelete, setDietToDelete] = useState<Diet | null>(null);

  const [isForceDeleteModalOpen, setIsForceDeleteModalOpen] = useState(false);

  const pageSize = 50;

  // Fetch diets
  const { data: dietsData, isLoading, error } = useQuery({
    queryKey: ['diets', currentPage, searchTerm, sortBy, sortOrder, filters],
    queryFn: () => dietService.getDiets({
      page: currentPage,
      limit: pageSize,
      search: searchTerm,
      sortBy,
      sortOrder,
      name: filters.name,
      code: filters.code,
      status: filters.status,
    }),
  });

  // Delete mutation (prompts to force when menus exist)
  const deleteMutation = useMutation({
    mutationFn: (id: string) => dietService.deleteDiet(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diets'] });
      setIsDeleteModalOpen(false);
      setDietToDelete(null);
      showSuccess('Diet deleted', 'The diet has been successfully deleted.');
    },
    onError: (error: any) => {
      const message = error?.message || 'Failed to delete diet. Please try again.';
      if (/menus/i.test(message)) {
        // Offer force delete
        setIsDeleteModalOpen(false);
        setIsForceDeleteModalOpen(true);
      } else {
        showError('Delete failed', message);
      }
    },
  });

  // Force delete mutation
  const forceDeleteMutation = useMutation({
    mutationFn: (id: string) => dietService.deleteDiet(id, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['diets'] });
      setIsForceDeleteModalOpen(false);
      setDietToDelete(null);
      showSuccess('Diet deleted', 'The diet and all its menus were deleted.');
    },
    onError: (error: any) => {
      const message = error?.message || 'Failed to force delete diet. Please try again.';
      showError('Force delete failed', message);
    },
  });


  const filteredRows = useMemo(() => {
    const rows = dietsData?.diets || [];
    if (!typeFilter.length) return rows;
    return rows.filter((d) => {
      const ts = (d.dietTypes || []).map((x) => x.type as DietType);
      return typeFilter.some((t) => ts.includes(t));
    });
  }, [dietsData, typeFilter]);

  const allIdsOnPage = useMemo(() => filteredRows.map((d) => d.id), [filteredRows]);

  const handleSelectAll = () => {
    if (selectedIds.length === allIdsOnPage.length) setSelectedIds([]);
    else setSelectedIds(allIdsOnPage);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleEdit = useCallback((diet: Diet) => {
    setSelectedDiet(diet);
    setIsModalOpen(true);
  }, []);

  const handleDelete = useCallback((diet: Diet) => {
    setDietToDelete(diet);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCreate = () => {
    setSelectedDiet(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedDiet(null);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['diets'] });
    handleModalClose();
  };

  const columns = useMemo<ColumnDef<Diet>[]>(() => [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      meta: { sortable: true },
      cell: (info) => info.getValue() as string,
    },
    {
      id: 'code',
      accessorKey: 'code',
      header: 'Code',
      meta: { sortable: true },
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      id: 'description',
      accessorKey: 'description',
      header: 'Description',
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      id: 'types',
      header: 'Types',
      cell: ({ row }) => {
        const types = (row.original as Diet).dietTypes || [];
        if (!types.length) return '-';
        return types.map((t) => DIET_TYPE_LABELS[(t.type as DietType)] || t.type).join(', ');
      },
    },
    {
      id: 'periodWeeks',
      accessorKey: 'periodWeeks',
      header: 'Period (Weeks)',
      meta: { sortable: true },
      cell: (info) => (info.getValue() as number | null) ?? '-',
    },
    {
      id: 'startingDay',
      accessorKey: 'startingDay',
      header: 'Starting Day',
      meta: { sortable: true },
      cell: (info) => (info.getValue() as string) || '-',
    },
    {
      id: 'startingDate',
      accessorKey: 'startingDate',
      header: 'Starting Date',
      meta: { sortable: true },
      cell: (info) => {
        const v = info.getValue() as string | null | undefined;
        return v ? new Date(v).toLocaleDateString() : '-';
      },
    },
    {
      id: 'statusDate',
      accessorKey: 'statusDate',
      header: 'Status Date',
      meta: { sortable: true },
      cell: (info) => {
        const v = info.getValue() as string | null | undefined;
        return v ? new Date(v).toLocaleString() : '-';
      },
    },
    {
      id: 'menus',
      header: 'Menus',
      cell: ({ row }) => (row.original as Diet)._count?.menus || 0,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      meta: { sortable: true },
      cell: ({ row }) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            (row.original as Diet).status === 'ACTIVE'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {(row.original as Diet).status}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center justify-end space-x-2">
          <button onClick={() => handleEdit(row.original as Diet)} className="text-primary-600 hover:text-primary-900">
            <PencilIcon className="w-4 h-4" />
          </button>
          <button onClick={() => handleDelete(row.original as Diet)} className="text-red-600 hover:text-red-900">
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ], [handleEdit, handleDelete]);

  const handleExport = async () => {
    try {
      const blob = await dietService.exportDiets({
        search: searchTerm,
        ids: selectedIds.length ? selectedIds : undefined,
        name: filters.name,
        code: filters.code,
        status: filters.status,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `diets-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const SortIcon: React.FC<{ column: string }> = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ?
      <ArrowUpIcon className="w-4 h-4 ml-1" /> :
      <ArrowDownIcon className="w-4 h-4 ml-1" />;
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Error loading diets. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Diets</h1>
        <div className="flex space-x-3">
          <Button
            variant="secondary"
            disabled={selectedIds.length === 0}
            onClick={() => handleBulkAction('activate', selectedIds)}
          >
            Activate
          </Button>
          <Button
            variant="secondary"
            disabled={selectedIds.length === 0}
            onClick={() => handleBulkAction('deactivate', selectedIds)}
          >
            Deactivate
          </Button>
          <Button variant="secondary" onClick={handleExport}>
            Export to Excel
          </Button>
          <Button onClick={handleCreate}>
            <PlusIcon className="w-5 h-5 mr-2" />
            Create
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search diets..."
              className="pl-10 form-input"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            value={filters.name || ''}
            onChange={(e) => { setFilters((f) => ({ ...f, name: e.target.value })); setCurrentPage(1); }}
            placeholder="Filter name"
            className="form-input form-input-sm"
            type="text"
          />
          <input
            value={filters.code || ''}
            onChange={(e) => { setFilters((f) => ({ ...f, code: e.target.value })); setCurrentPage(1); }}
            placeholder="Filter code"
            className="form-input form-input-sm"
            type="text"
          />
          <select
            className="form-input form-input-sm"
            value={filters.status ?? ''}
            onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value || undefined })); setCurrentPage(1); }}
          >
            <option value="">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
          <div className="flex items-center">
            <Button variant="secondary" size="sm" onClick={clearFilters}>Clear</Button>
          </div>
        </div>

        {/* Type filter */}
        <div className="mt-3">
          <div className="text-xs text-gray-600 mb-1">Types</div>
          <div className="flex flex-wrap gap-3">
            {DIET_TYPE_OPTIONS.map(opt => {
              const checked = typeFilter.includes(opt.value);
              return (
                <label key={opt.value} className="inline-flex items-center space-x-2">
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4"
                    checked={checked}
                    onChange={(e) => {
                      setTypeFilter((prev) => {
                        if (e.target.checked) return [...prev, opt.value];
                        return prev.filter((t) => t !== opt.value);
                      });
                    }}
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              );
            })}
          </div>
        </div>

      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-soft overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <>
            <DataGrid
              data={filteredRows}
              columns={columns}
              getRowId={(r) => (r as Diet).id}
              enableSelection
              selectedIds={selectedIds}
              onToggleRow={(id) => handleSelectOne(id)}
              onToggleAll={(ids, checked) => setSelectedIds(checked ? ids : [])}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSort}
            />

            {/* Legacy table retained temporarily (disabled) */}
            {false && (

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {/* Selection checkbox header */}
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        checked={selectedIds.length > 0 && selectedIds.length === allIdsOnPage.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center">
                        Name
                        <SortIcon column="name" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('code')}
                    >
                      <div className="flex items-center">
                        Code
                        <SortIcon column="code" />
                      </div>
                    </th>

                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('periodWeeks')}
                    >
                      <div className="flex items-center">
                        Period (Weeks)
                        <SortIcon column="periodWeeks" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('startingDay')}
                    >
                      <div className="flex items-center">
                        Starting Day
                        <SortIcon column="startingDay" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('startingDate')}
                    >
                      <div className="flex items-center">
                        Starting Date
                        <SortIcon column="startingDate" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('statusDate')}
                    >
                      <div className="flex items-center">
                        Status Date
                        <SortIcon column="statusDate" />
                      </div>
                    </th>


                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Menus
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center">
                        Status
                        <SortIcon column="status" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                  {/* Filter row */}
                  <tr>
                    <th className="px-4 py-2"></th>
                    <th className="px-6 py-2">
                      <input
                        value={filters.name || ''}
                        onChange={(e) => setFilters((f) => ({ ...f, name: e.target.value }))}
                        placeholder="Filter name"
                        className="form-input form-input-sm"
                        type="text"
                      />
                    </th>
                    <th className="px-6 py-2">
                      <input
                        value={filters.code || ''}
                        onChange={(e) => setFilters((f) => ({ ...f, code: e.target.value }))}
                        placeholder="Filter code"
                        className="form-input form-input-sm"
                        type="text"
                      />
                    </th>
                    <th className="px-6 py-2"></th>
                    <th className="px-6 py-2"></th>
                    <th className="px-6 py-2"></th>
                    <th className="px-6 py-2"></th>
                    <th className="px-6 py-2"></th>
                    <th className="px-6 py-2">
                      <input
                        value={filters.status || ''}
                        onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                        placeholder="Filter status"
                        className="form-input form-input-sm"
                        type="text"
                      />
                    </th>
                    <th className="px-6 py-2 text-right">
                      <Button variant="secondary" size="sm" onClick={clearFilters}>Clear</Button>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRows.map((diet) => {
                    const isSelected = selectedIds.includes(diet.id);
                    return (
                      <tr key={diet.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-primary-50' : ''}`}>
                        {/* Row selection */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            checked={isSelected}
                            onChange={() => handleSelectOne(diet.id)}
                          />



                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {diet.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {diet.code || '-'}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {diet.description || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {diet.periodWeeks ?? '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {diet.startingDay || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {diet.startingDate ? new Date(diet.startingDate).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {diet.statusDate ? new Date(diet.statusDate).toLocaleString() : '-'}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {diet._count?.menus || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            diet.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {diet.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleEdit(diet)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(diet)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            )}


            {/* Pagination */}
            {dietsData && dietsData.pagination.pages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <Button
                    variant="secondary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={currentPage === dietsData.pagination.pages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">
                        {(currentPage - 1) * pageSize + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium">
                        {Math.min(currentPage * pageSize, dietsData.pagination.total)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium">{dietsData.pagination.total}</span>{' '}
                      results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <Button
                        variant="secondary"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                        className="rounded-r-none"
                      >
                        Previous
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={currentPage === dietsData.pagination.pages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                        className="rounded-l-none"
                      >
                        Next
                      </Button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bulk actions overlay */}
      <BulkActions
        selectedItems={selectedIds}
        totalItems={dietsData?.pagination?.total || filteredRows.length}
        actions={[commonBulkActions.activate, commonBulkActions.deactivate, commonBulkActions.delete, commonBulkActions.refresh]}
        onAction={handleBulkAction}
        onSelectAll={handleSelectAll}
        onClearSelection={() => setSelectedIds([])}
        isVisible={selectedIds.length > 0}
      />

	      {isForceDeleteModalOpen && dietToDelete && (
	        <DeleteConfirmModal
	          title="Force Delete Diet"
	          message={`This will delete "${dietToDelete.name}" and ALL its menus, recipes and schedules. This cannot be undone. Continue?`}
	          onConfirm={() => forceDeleteMutation.mutate(dietToDelete.id)}
	          onCancel={() => { setIsForceDeleteModalOpen(false); setDietToDelete(null); }}
	          loading={forceDeleteMutation.isPending}
	        />
	      )}



      {/* Modals */}
      {isModalOpen && (
        <DietModal
          diet={selectedDiet}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {isDeleteModalOpen && dietToDelete && (
        <DeleteConfirmModal
          title="Delete Diet"
          message={`Are you sure you want to delete "${dietToDelete.name}"? This action cannot be undone.`}
          onConfirm={() => deleteMutation.mutate(dietToDelete.id)}
          onCancel={() => {
            setIsDeleteModalOpen(false);
            setDietToDelete(null);
          }}
          loading={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default DietsPage;
